import { useState } from 'react';
import { Plus, Edit, Trash2, Search, Mail, User } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Input,
  PasswordInput,
  Textarea,
  Select,
  Checkbox,
  RadioGroup,
  FormGroup,
  Modal,
  ConfirmModal,
  Table,
  TableActions,
  Pagination,
} from './core';

/**
 * Component Showcase
 * 
 * This component demonstrates all core UI components
 * Available in the component library.
 */
export function ComponentShowcase() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedProject, setSelectedProject] = useState('');
  const [requestType, setRequestType] = useState('vacation');
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Sample table data
  const tableData = [
    { id: 1, name: 'Michael Schneider', email: 'michael.s@firma.de', department: 'Entwicklung', status: 'active' },
    { id: 2, name: 'Sarah Müller', email: 'sarah.m@firma.de', department: 'Design', status: 'active' },
    { id: 3, name: 'Thomas Weber', email: 'thomas.w@firma.de', department: 'Marketing', status: 'inactive' },
    { id: 4, name: 'Anna Fischer', email: 'anna.f@firma.de', department: 'Vertrieb', status: 'active' },
  ];

  const tableColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'E-Mail', sortable: true },
    { key: 'department', label: 'Abteilung' },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            value === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {value === 'active' ? 'Aktiv' : 'Inaktiv'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right' as const,
      render: (_: any, row: any) => (
        <TableActions
          actions={[
            { label: 'Bearbeiten', icon: <Edit className="size-4" />, onClick: () => console.log('Edit', row) },
            { label: 'Löschen', icon: <Trash2 className="size-4" />, onClick: () => console.log('Delete', row), variant: 'danger' },
          ]}
        />
      ),
    },
  ];

  const projectOptions = [
    { value: 'alpha', label: 'Projekt Alpha' },
    { value: 'beta', label: 'Projekt Beta' },
    { value: 'gamma', label: 'Projekt Gamma' },
  ];

  const radioOptions = [
    { value: 'vacation', label: 'Urlaubsantrag' },
    { value: 'overtime', label: 'Überstundenantrag' },
    { value: 'sick', label: 'Krankmeldung' },
  ];

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-[#1c1c1c] text-[32px] font-medium mb-2">
            Component Showcase
          </h1>
          <p className="text-[#6b6b6b] text-[16px]">
            Alle standardisierten UI-Komponenten der Bibliothek
          </p>
        </div>

        {/* Buttons Section */}
        <Card>
          <CardHeader title="Buttons" subtitle="Alle Button-Varianten und -Größen" borderBottom />
          <CardContent>
            <div className="space-y-6">
              {/* Variants */}
              <div>
                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-3">Varianten</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="danger">Danger</Button>
                  <Button variant="success">Success</Button>
                  <Button variant="outline">Outline</Button>
                </div>
              </div>

              {/* Sizes */}
              <div>
                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-3">Größen</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="primary" size="sm">Small</Button>
                  <Button variant="primary" size="md">Medium</Button>
                  <Button variant="primary" size="lg">Large</Button>
                </div>
              </div>

              {/* With Icons */}
              <div>
                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-3">Mit Icons</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" icon={<Plus className="size-4" />}>
                    Hinzufügen
                  </Button>
                  <Button variant="secondary" icon={<Edit className="size-4" />}>
                    Bearbeiten
                  </Button>
                  <Button variant="danger" icon={<Trash2 className="size-4" />} iconPosition="right">
                    Löschen
                  </Button>
                </div>
              </div>

              {/* States */}
              <div>
                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-3">Zustände</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" isLoading>Loading</Button>
                  <Button variant="primary" disabled>Disabled</Button>
                  <Button variant="primary" fullWidth>Full Width</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Components Section */}
        <Card>
          <CardHeader title="Formular-Komponenten" subtitle="Eingabefelder und Formularelemente" borderBottom />
          <CardContent>
            <FormGroup columns={2}>
              <Input label="Vorname" placeholder="Max" />
              <Input label="Nachname" placeholder="Mustermann" />
              
              <Input 
                label="E-Mail-Adresse" 
                type="email" 
                placeholder="max@firma.de"
                leftIcon={<Mail className="size-4" />}
              />
              
              <PasswordInput 
                label="Passwort" 
                placeholder="Passwort eingeben"
              />

              <Input 
                label="Benutzername mit Fehler" 
                placeholder="Benutzername"
                error="Dieser Benutzername ist bereits vergeben"
              />

              <Input 
                label="Mit Hinweis" 
                placeholder="Eingabe"
                hint="Mindestens 8 Zeichen erforderlich"
              />

              <Select
                label="Projekt auswählen"
                placeholder="Bitte wählen..."
                options={projectOptions}
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
              />

              <Input 
                label="Suchfeld" 
                placeholder="Suchen..."
                leftIcon={<Search className="size-4" />}
              />
            </FormGroup>

            <div className="mt-6">
              <Textarea
                label="Beschreibung"
                placeholder="Projektbeschreibung eingeben..."
                rows={4}
                hint="Maximal 500 Zeichen"
              />
            </div>

            <div className="mt-6 space-y-4">
              <Checkbox
                label="Ich akzeptiere die Nutzungsbedingungen"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />

              <RadioGroup
                label="Antragsart"
                name="requestType"
                value={requestType}
                onChange={setRequestType}
                options={radioOptions}
                orientation="vertical"
              />
            </div>
          </CardContent>
        </Card>

        {/* Cards Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader 
              title="Einfache Karte" 
              subtitle="Mit Titel und Untertitel"
              borderBottom
            />
            <CardContent>
              <p className="text-[#6b6b6b] text-[14px]">
                Dies ist der Inhalt einer einfachen Karte. Karten können verschiedene
                Inhaltstypen enthalten und sind die Grundlage für die meisten UI-Elemente.
              </p>
            </CardContent>
          </Card>

          <Card hover>
            <CardHeader 
              title="Karte mit Action" 
              action={
                <Button variant="ghost" size="sm">
                  Anzeigen
                </Button>
              }
            />
            <CardContent>
              <p className="text-[#6b6b6b] text-[14px]">
                Diese Karte hat einen Hover-Effekt und eine Action im Header.
                Ideal für klickbare Karten oder Listen.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Karte mit Footer" borderBottom />
            <CardContent>
              <p className="text-[#6b6b6b] text-[14px]">
                Karten können einen Footer mit Aktionsbuttons haben.
              </p>
            </CardContent>
            <CardFooter borderTop alignment="right">
              <Button variant="secondary" size="sm">Abbrechen</Button>
              <Button variant="primary" size="sm">Speichern</Button>
            </CardFooter>
          </Card>

          <Card padding="md">
            <div className="text-center">
              <div className="size-16 bg-[#0084c7] rounded-full flex items-center justify-center text-white text-[24px] font-medium mx-auto mb-3">
                42
              </div>
              <h3 className="text-[#1c1c1c] text-[18px] font-medium mb-1">Statistik-Karte</h3>
              <p className="text-[#6b6b6b] text-[14px]">Mit eigenem Padding</p>
            </div>
          </Card>
        </div>

        {/* Table Section */}
        <Card>
          <CardHeader 
            title="Datentabelle" 
            subtitle="Mit Sortierung und Aktionen"
            action={
              <Button variant="primary" size="sm" icon={<Plus className="size-4" />}>
                Neu
              </Button>
            }
            borderBottom
          />
          <CardContent noPadding>
            <Table
              columns={tableColumns}
              data={tableData}
              onRowClick={(row) => console.log('Clicked:', row)}
              striped
              hoverable
            />
          </CardContent>
          <CardFooter borderTop>
            <Pagination
              currentPage={currentPage}
              totalPages={5}
              onPageChange={setCurrentPage}
              showPageSize
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </CardFooter>
        </Card>

        {/* Modals Section */}
        <Card>
          <CardHeader title="Modals" subtitle="Dialog-Fenster und Bestätigungen" borderBottom />
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                Standard Modal öffnen
              </Button>
              <Button variant="danger" onClick={() => setIsConfirmOpen(true)}>
                Bestätigungs-Modal öffnen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Standard Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Zeiterfassung bearbeiten"
          subtitle="Ändern Sie Ihre Arbeitsstunden"
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                Abbrechen
              </Button>
              <Button variant="primary" onClick={() => setIsModalOpen(false)}>
                Speichern
              </Button>
            </>
          }
        >
          <FormGroup columns={1}>
            <Select
              label="Projekt"
              placeholder="Projekt auswählen..."
              options={projectOptions}
            />
            <Input
              label="Arbeitsstunden"
              type="number"
              step="0.5"
              min="0"
              max="24"
              placeholder="8.0"
            />
            <Textarea
              label="Beschreibung"
              placeholder="Was haben Sie gemacht?"
              rows={4}
            />
          </FormGroup>
        </Modal>

        {/* Confirm Modal */}
        <ConfirmModal
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={() => {
            console.log('Confirmed!');
            setIsConfirmOpen(false);
          }}
          title="Eintrag löschen?"
          message="Möchten Sie diesen Zeiterfassungseintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
          confirmText="Löschen"
          cancelText="Abbrechen"
          variant="danger"
        />
      </div>
    </div>
  );
}
